/**
 * DecisionModal — applies the seam-2 link decision via the single-use callback token
 * (POST /referral/liens/decision). The token is the security (the central Novera platform normally
 * carries it); the screen still capability-keys the action on referral.lien.manage so an operator can
 * resolve a pending invite when wiring/testing the seam. APPROVED → approuve (+ optional novera_ref);
 * REFUSED → rejete (terminal).
 */
import { useEffect, useId, useState } from "react";
import { Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { useT } from "@/app";
import { useApplyDecision, type DecisionVars } from "../api";

export interface DecisionModalProps {
  open: boolean;
  labId: string | null;
  disabled?: boolean;
  /** Pre-fill the médecin name for context (display only). */
  medecinNom?: string | undefined;
  onClose: () => void;
  onDecided?: () => void;
}

export function DecisionModal({
  open,
  labId,
  disabled,
  medecinNom,
  onClose,
  onDecided,
}: DecisionModalProps) {
  const { t } = useT();
  const toast = useToast();
  const apply = useApplyDecision(labId);
  const formId = useId();

  const [token, setToken] = useState("");
  const [decision, setDecision] = useState<"APPROVED" | "REFUSED">("APPROVED");
  const [noveraRef, setNoveraRef] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setToken("");
      setDecision("APPROVED");
      setNoveraRef("");
      setTouched(false);
    }
  }, [open]);

  const tokenError =
    touched && token.trim().length < 8 ? t("referral.field.required") : undefined;
  const canSubmit = !disabled && token.trim().length >= 8 && !apply.isPending;

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    const vars: DecisionVars = { callback_token: token.trim(), decision };
    if (decision === "APPROVED" && noveraRef.trim()) vars.novera_ref = noveraRef.trim();
    apply.mutate(vars, {
      onSuccess: (res) => {
        toast.success(
          t("referral.decision.success", { statut: t(`referral.lien.statut.${res.statut}`) }),
        );
        onDecided?.();
        onClose();
      },
      onError: (err) => toast.error(err.message, t("referral.decision.title")),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={medecinNom ? `${t("referral.decision.title")} — ${medecinNom}` : t("referral.decision.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("referral.action.cancel")}
          </Button>
          <Button
            variant="primary"
            form={formId}
            type="submit"
            loading={apply.isPending}
            disabled={!canSubmit}
          >
            {t("referral.decision.submit")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
      >
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("referral.decision.desc")}</p>
        <FormField
          label={t("referral.decision.token")}
          required
          hint={t("referral.decision.token.hint")}
          error={tokenError}
        >
          {(p) => (
            <Input
              {...p}
              mono
              className="nv-ltr-island"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoFocus
              invalid={!!tokenError}
            />
          )}
        </FormField>
        <FormField label={t("referral.decision.choice")} required>
          {(p) => (
            <Select
              {...p}
              value={decision}
              onChange={(e) => setDecision(e.target.value as "APPROVED" | "REFUSED")}
              options={[
                { value: "APPROVED", label: t("referral.decision.approve") },
                { value: "REFUSED", label: t("referral.decision.refuse") },
              ]}
            />
          )}
        </FormField>
        {decision === "APPROVED" && (
          <FormField
            label={t("referral.decision.noveraRef")}
            hint={t("referral.decision.noveraRef.hint")}
          >
            {(p) => (
              <Input
                {...p}
                className="nv-ltr-island"
                value={noveraRef}
                onChange={(e) => setNoveraRef(e.target.value)}
              />
            )}
          </FormField>
        )}
      </form>
    </Modal>
  );
}
